import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, ArrowUp, ArrowDown, Trash2, Eye, EyeOff } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { adminListStoreClips, adminUpdateStoreClip, adminReorderStoreClips, adminDeleteStoreClip, type StoreClip } from '@/services/creatorApi';

// Store clips review. tools/store-clips registers new cuts unpublished; this is
// where they get watched, published, ordered or dropped. Published clips show on
// the creator's store in this order.

export function StoreClipsPanel() {
  const { toast } = useToast();
  const [slugs, setSlugs] = useState<string[]>([]);
  const [slug, setSlug] = useState('');
  const [clips, setClips] = useState<StoreClip[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = async (s = slug) => {
    setLoading(true);
    try {
      const r = await fetch(`/api/admin/creator-clips${s ? `?slug=${encodeURIComponent(s)}` : ''}`, { credentials: 'include' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Failed to load');
      setClips(d.clips || []);
      setSlugs(d.slugs || []);
      if (!s && d.slugs?.length) { setSlug(d.slugs[0]); setClips((d.clips || []).filter((c: StoreClip) => c.creatorSlug === d.slugs[0])); }
    } catch (e: any) { toast({ title: 'Could not load clips', description: e.message, variant: 'destructive' }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(''); }, []);

  const pick = (s: string) => { setSlug(s); adminListStoreClips(s).then(setClips).catch(() => {}); };

  const togglePublished = async (c: StoreClip) => {
    setBusy(c._id);
    try { const u = await adminUpdateStoreClip(c._id, { published: !c.published }); setClips(cs => cs.map(x => (x._id === c._id ? { ...x, published: u.published } : x))); }
    catch (e: any) { toast({ title: 'Not saved', description: e.message, variant: 'destructive' }); }
    finally { setBusy(null); }
  };

  const move = async (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= clips.length) return;
    const n = [...clips]; [n[i], n[j]] = [n[j], n[i]];
    setClips(n);
    try { await adminReorderStoreClips(slug, n.map(c => c._id)); }
    catch (e: any) { toast({ title: 'Order not saved', description: e.message, variant: 'destructive' }); }
  };

  const remove = async (c: StoreClip) => {
    setBusy(c._id);
    try { await adminDeleteStoreClip(c._id); setClips(cs => cs.filter(x => x._id !== c._id)); }
    catch (e: any) { toast({ title: 'Not deleted', description: e.message, variant: 'destructive' }); }
    finally { setBusy(null); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Store clips</CardTitle>
        <CardDescription>Captioned cuts from replays. New clips arrive unpublished; published ones show on the creator's store in this order.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {slugs.length > 1 && (
          <div className="flex flex-wrap gap-2">
            {slugs.map(s => <Button key={s} size="sm" variant={s === slug ? 'default' : 'outline'} onClick={() => pick(s)}>{s}</Button>)}
          </div>
        )}
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : clips.length === 0 ? (
          <p className="text-sm">No clips yet. Make them with tools/store-clips (see its README).</p>
        ) : (
          <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))' }}>
            {clips.map((c, i) => (
              <div key={c._id} className="space-y-2">
                <video src={c.videoUrl} poster={c.posterUrl || undefined} controls playsInline preload="metadata" style={{ width: '100%', aspectRatio: '9 / 16', borderRadius: 8, background: '#000' }} />
                <div className="text-sm font-semibold leading-snug">{c.title}</div>
                {c.productTitle && <div className="text-xs">{c.productTitle}{typeof c.startSec === 'number' ? ` · ${Math.floor(c.startSec / 60)}:${String(Math.floor(c.startSec % 60)).padStart(2, '0')}` : ''}</div>}
                <Badge variant={c.published ? 'default' : 'secondary'}>{c.published ? 'Published' : 'Unpublished'}</Badge>
                <div className="flex gap-1">
                  <Button size="icon" variant="outline" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move earlier"><ArrowUp className="w-4 h-4" /></Button>
                  <Button size="icon" variant="outline" onClick={() => move(i, 1)} disabled={i === clips.length - 1} aria-label="Move later"><ArrowDown className="w-4 h-4" /></Button>
                  <Button size="icon" variant="outline" onClick={() => togglePublished(c)} disabled={busy === c._id} aria-label={c.published ? 'Unpublish' : 'Publish'}>
                    {c.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button size="icon" variant="outline" onClick={() => remove(c)} disabled={busy === c._id} aria-label="Delete"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
